import { z } from 'zod';
/**
 * Community REST DTO — Phase 3 v1.1 / Plan 03-02 (COMM-02).
 *
 * 엔드포인트 카테고리:
 *
 *   **Boards (CRUD — 본 Task 1)**
 *     - POST   /api/v1/community/boards                     — 스트리머 게시판 생성
 *     - PATCH  /api/v1/community/boards/:id                 — 게시판 수정 (owner)
 *     - DELETE /api/v1/community/boards/:id                 — soft-delete (owner, active=false)
 *     - GET    /api/v1/community/boards?streamerId=<uuid>   — 공개 목록 조회 (required_tier/role 무관)
 *     - GET    /api/v1/community/boards/:id                 — 단건 조회 (BoardAccessGuard)
 *
 *   **Posts (Task 2)**
 *     - POST   /api/v1/community/boards/:boardId/posts      — 게시글 작성 (BoardAccessGuard)
 *     - GET    /api/v1/community/boards/:boardId/posts      — 목록 (cursor pagination)
 *     - GET    /api/v1/community/posts/:id                  — 단건 조회 + view_count++
 *     - DELETE /api/v1/community/posts/:id                  — 본인 soft-delete
 *     - GET    /api/v1/community/posts/search?q=&streamerId=— FTS 검색 (tsvector + plainto_tsquery)
 *     - POST   /api/v1/community/posts/:id/like             — 좋아요 토글 (추후 확장)
 *
 *   **Comments (Task 3)**
 *     - POST   /api/v1/community/comments                   — 루트/답글 작성 (2-depth 강제)
 *     - GET    /api/v1/community/posts/:postId/comments     — tree 조회
 *     - PATCH  /api/v1/community/comments/:id               — 본인 수정
 *     - DELETE /api/v1/community/comments/:id               — 본인 soft-delete
 *
 * **계약 불변**:
 *   - `required_tier/required_role` 는 DB 가 모호하지 않게 선언하므로 Zod 에서도 nullable · enum 동일 집합.
 *   - slug 는 lowercase alphanumeric + hyphen (URL-safe). 3~50자.
 *   - cursor 는 base64(`${publishedAtIso}:${id}`) — 애플리케이션 파싱/생성.
 *   - 응답 DTO 에 `requiredTier: { id, name, priceKrw }` 포함 (UI 편의 — tier 조회 왕복 없음).
 *   - 익명 게시글은 DTO 에서 authorId=null + authorHandle='익명' 마스킹 (DB 원본 유지).
 *   - `shadowed` 플래그는 **작성자 본인 응답** 에만 true 로 노출 (목록 조회에는 실어 보내지 않음).
 *
 * Wave 1 범위 (본 plan): 응답은 Zod 런타임 검증 없이 타입만 소비 (controller Zod 파싱은 request 만 적용).
 *   후속 UI plan (03-04/05) 이 fetcher 레이어에서 response schema 로 재검증 가능.
 */
// ---------------------------------------------------------------------------
// Shared enums / primitives
// ---------------------------------------------------------------------------
/** DB schema 의 `board_type` enum 과 동일 집합. */
export const boardTypeSchema = z.enum([
    'notice',
    'free',
    'gallery',
    'event',
    'qna',
    'custom',
]);
/** DB schema 의 `board_required_role` enum 과 동일 집합. */
export const boardRequiredRoleSchema = z.enum([
    'regular',
    'member',
    'vip',
    'streamer',
]);
/** slug: lowercase alphanumeric + hyphen (URL-safe), 3~50자. */
export const boardSlugSchema = z
    .string()
    .min(3)
    .max(50)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, {
    message: 'slug must be lowercase alphanumeric + hyphen',
});
// ---------------------------------------------------------------------------
// Board DTO
// ---------------------------------------------------------------------------
/**
 * `requiredTier` 응답 축소 DTO — UI 가 "VIP 1만원" 같은 라벨을 바로 렌더.
 * DB 의 `subscriptionTiers` LEFT JOIN 으로 한 번에 반환하므로 Board API 소비자가
 * 별도 `GET /tiers/:id` 왕복을 하지 않는다.
 */
export const boardTierSnapshotSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(30),
    /** bigint → string 직렬화 (TierController 패턴 계승). */
    priceKrw: z.string().regex(/^\d+$/),
});
export const boardDtoSchema = z.object({
    id: z.string().uuid(),
    streamerId: z.string().uuid(),
    slug: boardSlugSchema,
    name: z.string().min(1).max(50),
    type: boardTypeSchema,
    requiredTier: boardTierSnapshotSchema.nullable(),
    requiredRole: boardRequiredRoleSchema,
    anonymousAllowed: z.boolean(),
    position: z.number().int().min(0).max(1000),
    active: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// Board requests
// ---------------------------------------------------------------------------
export const createBoardRequestSchema = z.object({
    slug: boardSlugSchema,
    name: z.string().min(1).max(50),
    type: boardTypeSchema,
    /** null or uuid — null 이면 gate 없음. */
    requiredTierId: z.string().uuid().nullable().default(null),
    requiredRole: boardRequiredRoleSchema.default('regular'),
    anonymousAllowed: z.boolean().default(false),
    position: z.number().int().min(0).max(1000).default(0),
});
/**
 * PATCH — 모든 필드 optional. slug 변경은 URL 쿠키/링크 단절 리스크가 있으나
 * 본 plan 에서 허용 (Wave 2 에 "slug 변경 시 리다이렉트" 도입 고려).
 */
export const updateBoardRequestSchema = z.object({
    slug: boardSlugSchema.optional(),
    name: z.string().min(1).max(50).optional(),
    type: boardTypeSchema.optional(),
    requiredTierId: z.string().uuid().nullable().optional(),
    requiredRole: boardRequiredRoleSchema.optional(),
    anonymousAllowed: z.boolean().optional(),
    position: z.number().int().min(0).max(1000).optional(),
    active: z.boolean().optional(),
});
export const listBoardsQuerySchema = z.object({
    streamerId: z.string().uuid(),
    includeInactive: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((v) => v === true || v === 'true'),
});
export const listBoardsResponseSchema = z.object({
    items: z.array(boardDtoSchema),
    count: z.number().int().min(0),
});
// ---------------------------------------------------------------------------
// Post DTO
// ---------------------------------------------------------------------------
/** PostDTO 에서 작성자 노출 — 익명 글은 마스킹되어 들어옴 (service 레이어 책임). */
export const postAuthorSnapshotSchema = z.object({
    /** 익명 글은 null. */
    id: z.string().uuid().nullable(),
    /** 익명 글은 '익명'. */
    handle: z.string(),
});
export const postDtoSchema = z.object({
    id: z.string().uuid(),
    boardId: z.string().uuid(),
    streamerId: z.string().uuid(),
    author: postAuthorSnapshotSchema,
    title: z.string().min(1).max(200),
    /** markdown 본문. listByBoard 에서는 truncate 된 preview 일 수 있음 (서비스 정책). */
    content: z.string(),
    isAnonymous: z.boolean(),
    isPinned: z.boolean(),
    viewCount: z.number().int().min(0),
    likeCount: z.number().int().min(0),
    commentCount: z.number().int().min(0),
    publishedAt: z.string().datetime({ offset: true }),
    editedAt: z.string().datetime({ offset: true }).nullable(),
});
// ---------------------------------------------------------------------------
// Post requests
// ---------------------------------------------------------------------------
export const createPostRequestSchema = z.object({
    /** title — 1~200자. */
    title: z.string().min(1).max(200),
    /** content — markdown, 1~20000자. */
    content: z.string().min(1).max(20_000),
    isAnonymous: z.boolean().default(false),
    isPinned: z.boolean().default(false),
});
export const updatePostRequestSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    content: z.string().min(1).max(20_000).optional(),
    isPinned: z.boolean().optional(),
});
/**
 * 커서 — base64(`${publishedAtIso}:${id}`). application 계층이 base64 디코드 후 파싱.
 * 클라이언트가 임의로 생성하지 않는다 (서버가 응답에 포함).
 */
export const cursorSchema = z.string().min(1).max(256);
export const listPostsQuerySchema = z.object({
    boardId: z.string().uuid(),
    cursor: cursorSchema.optional(),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
});
export const listPostsResponseSchema = z.object({
    items: z.array(postDtoSchema),
    nextCursor: z.string().nullable(),
});
export const searchPostsQuerySchema = z.object({
    q: z.string().min(1).max(200),
    streamerId: z.string().uuid(),
    boardId: z.string().uuid().optional(),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
});
export const searchPostsResponseSchema = z.object({
    items: z.array(postDtoSchema),
    /** 검색 결과는 cursor 대신 일회성 LIMIT — 무한 스크롤 없음 (Wave 1 간이). */
    count: z.number().int().min(0),
});
/**
 * 작성 응답 — `shadowed: true` 이면 작성자에게 "검토 대기중" UI 노출용 힌트.
 * 타 fan 목록 조회에는 노출되지 않는다.
 */
export const createPostResponseSchema = z.object({
    postId: z.string().uuid(),
    shadowed: z.boolean(),
});
// ---------------------------------------------------------------------------
// Comment DTO + requests
// ---------------------------------------------------------------------------
export const commentDtoSchema = z.object({
    id: z.string().uuid(),
    postId: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    author: postAuthorSnapshotSchema,
    content: z.string(),
    isAnonymous: z.boolean(),
    likeCount: z.number().int().min(0),
    createdAt: z.string().datetime({ offset: true }),
    /** soft-delete — NULL 아니면 UI 는 "삭제된 댓글입니다" placeholder. */
    deletedAt: z.string().datetime({ offset: true }).nullable(),
});
/** 2-depth tree — root 댓글 + 그 아래 replies 배열. */
export const commentTreeNodeSchema = commentDtoSchema.extend({
    replies: z.array(commentDtoSchema),
});
export const createCommentRequestSchema = z.object({
    postId: z.string().uuid(),
    content: z.string().min(1).max(2_000),
    parentId: z.string().uuid().nullable().default(null),
    isAnonymous: z.boolean().default(false),
});
export const updateCommentRequestSchema = z.object({
    content: z.string().min(1).max(2_000),
});
export const listCommentsQuerySchema = z.object({
    postId: z.string().uuid(),
    cursor: cursorSchema.optional(),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
});
export const listCommentsResponseSchema = z.object({
    items: z.array(commentTreeNodeSchema),
    nextCursor: z.string().nullable(),
});
export const createCommentResponseSchema = z.object({
    commentId: z.string().uuid(),
    shadowed: z.boolean(),
});
// ---------------------------------------------------------------------------
// Plan 03-03 — AMA (익명 질문/답변 선택)
// ---------------------------------------------------------------------------
/**
 * AMA 질문 작성 — POST /api/v1/community/ama/questions.
 *
 * boardId 는 type='qna' + anonymous_allowed=true 여야 함 (service 검증).
 * is_anonymous 는 board.anonymous_allowed=true 일 때 기본 true 강제 (service 정책).
 */
export const amaAskRequestSchema = z.object({
    boardId: z.string().uuid(),
    title: z.string().min(1).max(200),
    content: z.string().min(1).max(5_000),
});
export const amaAskResponseSchema = z.object({
    questionId: z.string().uuid(),
    postId: z.string().uuid(),
});
/**
 * AMA 대기 큐 아이템 — 스트리머 대시보드용. author 는 익명 마스킹 후 전달.
 */
export const amaQueueItemDtoSchema = z.object({
    questionId: z.string().uuid(),
    postId: z.string().uuid(),
    boardId: z.string().uuid(),
    title: z.string().min(1).max(200),
    content: z.string(),
    isAnonymous: z.boolean(),
    /** 익명이면 '익명', 아니면 author.slug. */
    askedByDisplay: z.string().min(1).max(32),
    createdAt: z.string().datetime({ offset: true }),
});
export const amaListQueueResponseSchema = z.object({
    items: z.array(amaQueueItemDtoSchema),
    count: z.number().int().min(0),
});
export const amaAnswerRequestSchema = z.object({
    answerText: z.string().min(1).max(500),
});
export const amaAnswerResponseSchema = z.object({
    questionId: z.string().uuid(),
    broadcastAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// Plan 03-03 — Streamer community settings (hot overlay · AMA active board)
// ---------------------------------------------------------------------------
export const streamerCommunitySettingsDtoSchema = z.object({
    streamerId: z.string().uuid(),
    hotOverlayEnabled: z.boolean(),
    hotOverlayFrequencySec: z.number().int().min(10).max(600),
    amaActiveBoardId: z.string().uuid().nullable(),
    updatedAt: z.string().datetime({ offset: true }),
});
export const updateStreamerCommunitySettingsRequestSchema = z.object({
    hotOverlayEnabled: z.boolean().optional(),
    hotOverlayFrequencySec: z.number().int().min(10).max(600).optional(),
    amaActiveBoardId: z.string().uuid().nullable().optional(),
});
// ---------------------------------------------------------------------------
// Plan 03-05 — Moderation (reports + shadowed posts) + streamer 전용 큐
// ---------------------------------------------------------------------------
/**
 * 스트리머 모더레이션 큐 — 본인 boards 범위의 post_reports · shadowed posts.
 *
 *   - GET    /api/v1/community/moderation/reports?resolved=false&pageSize=50
 *   - PATCH  /api/v1/community/moderation/reports/:id        { resolved: true }
 *   - GET    /api/v1/community/moderation/shadowed-posts?pageSize=50
 *   - PATCH  /api/v1/community/posts/:id/unshadow
 *
 * ownership: req.user.id → streamers.user_id 역조회 → board.streamer_id 필터.
 * 타 스트리머 row 조작 시 404 (정보 최소화) / 403.
 */
export const listReportsQuerySchema = z.object({
    resolved: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((v) => v === true || v === 'true'),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export const reportItemDtoSchema = z.object({
    reportId: z.string().uuid(),
    postId: z.string().uuid(),
    boardId: z.string().uuid(),
    reporterId: z.string().uuid().nullable(),
    reason: z.string().min(1).max(200),
    resolved: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    /** 검토 편의 — 원본 글 미리보기 (100자 excerpt). */
    postTitle: z.string(),
    postExcerpt: z.string(),
});
export const listReportsResponseSchema = z.object({
    items: z.array(reportItemDtoSchema),
    count: z.number().int().min(0),
});
export const resolveReportRequestSchema = z.object({
    resolved: z.literal(true),
});
export const listShadowedPostsQuerySchema = z.object({
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export const shadowedPostItemDtoSchema = z.object({
    postId: z.string().uuid(),
    boardId: z.string().uuid(),
    title: z.string(),
    excerpt: z.string(),
    authorHandle: z.string(),
    createdAt: z.string().datetime({ offset: true }),
});
export const listShadowedPostsResponseSchema = z.object({
    items: z.array(shadowedPostItemDtoSchema),
    count: z.number().int().min(0),
});
//# sourceMappingURL=community.rest.js.map